import { useState, useEffect, useCallback } from 'react';
import { useApp } from '../../context';
import { canAccess } from '../../utils/rbac';
import { overtimeAwaitingDecision } from '../../utils/navCounts';
import api from '../../hooks/useApi';
import { formatDate } from '../../utils/format';
import { formatHoursMinutes as hm } from '../../utils/overtime';
import Modal from '../shared/Modal';
import * as XLSX from 'xlsx';
import { useToast } from '../ui/Toast';

function downloadXlsx(sheets, filename) {
  const wb = XLSX.utils.book_new();
  sheets.forEach(({ name, rows }) => {
    const ws = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, name);
  });
  XLSX.writeFile(wb, filename);
}

// What a manager decides here is whether the work was overtime, and nothing
// about what it pays. The policy fixes that from the date worked, and the
// money is shown on the finance Overtime page, so no pricing appears on this
// screen for a manager to set, second-guess or leave unset.
const TYPE_LABELS  = {
  overtime:          'Overtime',
  standby:           'Standby',
  toil:              'TOIL',
  call_out:          'Call-Out',
  shift_move:        'Shift Move',
  working_away:      'Working Away',
  desk_move_weekday: 'Desk Move (Weekday)',
  desk_move_weekend: 'Desk Move (Weekend)',
  remote_working:    'Remote Working',
  out_of_hours:      'Out of Hours (>5PM / Sat)',
};
function formatDatetime(dtStr) {
  if (!dtStr) return '—';
  const d = new Date(dtStr.replace(' ', 'T'));
  if (isNaN(d)) return dtStr;
  return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false });
}

function StatusBadge({ status }) {
  const map = {
    pending:  'bg-amber-100 text-amber-700 border-amber-300',
    approved: 'bg-emerald-100 text-emerald-700 border-emerald-300',
    rejected: 'bg-red-100 text-red-700 border-red-300',
  };
  return (
    <span className={`inline-block px-1.5 py-0.5 text-[8px] font-bold uppercase rounded border ${map[status] || 'bg-gray-100 text-gray-500'}`}>
      {status}
    </span>
  );
}

// ── Review / View modal for managers and HR ───────────────────────────────
function OvertimeModal({ req, onClose, onSave, isManager }) {
  const { toast } = useToast();
  const [rejectionReason, setRejectionReason] = useState('');
  const [saving, setSaving] = useState(false);

  const handleApprove = async () => {
    setSaving(true);
    try {
      await onSave({ id: req.id, status: 'approved' });
    } catch (e) {
      // A refusal (a TOIL claim that would pass the grade's cap, say) is reported
      // here in the dialog, with the request still open for the manager to act on.
      toast.error(e.message || 'Could not approve this request.');
    }
    setSaving(false);
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) return;
    setSaving(true);
    await onSave({ id: req.id, status: 'rejected', rejection_reason: rejectionReason });
    setSaving(false);
  };

  return (
    <Modal
      title={`${TYPE_LABELS[req.request_type] || req.request_type} — ${req.person_name}`}
      onClose={onClose}
      footer={
        <div className="flex gap-2 justify-end w-full">
          <button onClick={onClose} className="px-3 py-1 text-[10px] font-semibold border border-gray-200 rounded bg-white">Close</button>
          {isManager && req?.status === 'pending' && (
            <>
              <button onClick={handleReject} disabled={saving || !rejectionReason.trim()}
                className="px-3 py-1 text-[10px] font-semibold bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-40">
                Reject
              </button>
              <button onClick={handleApprove} disabled={saving}
                className="px-3 py-1 text-[10px] font-semibold bg-emerald-500 text-emerald-950 rounded hover:bg-emerald-600 disabled:opacity-40">
                {saving ? 'Saving…' : 'Approve'}
              </button>
            </>
          )}
        </div>
      }
    >
      <div className="grid grid-cols-2 gap-2">
        {/* Type + Person */}
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Type</label>
          <div className="text-[11px] font-semibold">{TYPE_LABELS[req.request_type] || req.request_type}</div>
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Person</label>
          <div className="text-[11px] font-semibold">{req.person_name}</div>
        </div>

        {/* Start / End */}
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Start</label>
          <div className="text-[10px]">{formatDatetime(req.start_datetime || req.date)}</div>
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">End</label>
          <div className="text-[10px]">{req.end_datetime ? formatDatetime(req.end_datetime) : '—'}</div>
        </div>

        {/* Hours */}
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Duration</label>
          <div className="text-[11px] font-semibold">{hm(req.hours)}</div>
        </div>

        {/* Status */}
        <div className="flex items-end pb-0.5 gap-2">
          <StatusBadge status={req.status} />
          {req.approver_name && <span className="text-[8px] text-gray-400">by {req.approver_name}</span>}
        </div>

        {/* Description */}
        {req.reason && (
          <div className="col-span-2">
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Description</label>
            <div className="text-[10px] text-gray-700 bg-gray-50 rounded p-2">{req.reason}</div>
          </div>
        )}

        {/* Rejection reason input — for manager when reviewing */}
        {isManager && req?.status === 'pending' && (
          <div className="col-span-2">
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Rejection Reason (required to reject)</label>
            <textarea className="form-textarea" rows="2" value={rejectionReason}
              onChange={e => setRejectionReason(e.target.value)} placeholder="Provide a reason…" />
          </div>
        )}

        {/* Show rejection reason if already rejected */}
        {req?.status === 'rejected' && req?.rejection_reason && (
          <div className="col-span-2 bg-red-50 border border-red-200 rounded p-2">
            <div className="text-[8px] font-bold text-red-600 uppercase mb-0.5">Rejection Reason</div>
            <div className="text-[10px] text-red-700">{req.rejection_reason}</div>
          </div>
        )}
      </div>
    </Modal>
  );
}

// ── Report view ──────────────────────────────────────────────────────────────
function ReportView() {
  const [from, setFrom]   = useState(() => { const d = new Date(); d.setMonth(d.getMonth() - 1); d.setDate(1); return d.toISOString().split('T')[0]; });
  const [to, setTo]       = useState(() => new Date().toISOString().split('T')[0]);
  const [type, setType]   = useState('');
  const [data, setData]   = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    const p = new URLSearchParams({ from, to });
    if (type) p.set('type', type);
    const res = await api.get(`overtime-requests/report?${p.toString()}`);
    setData(res);
    setLoading(false);
  };

  useEffect(() => { run(); }, []);

  return (
    <div>
      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded p-3 mb-3 flex flex-wrap gap-2 items-end">
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">From</label>
          <input className="form-input w-32" type="date" value={from} onChange={e => setFrom(e.target.value)} />
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">To</label>
          <input className="form-input w-32" type="date" value={to} onChange={e => setTo(e.target.value)} />
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Type</label>
          <select className="form-select w-28" value={type} onChange={e => setType(e.target.value)}>
            <option value="">All Types</option>
            <option value="overtime">Overtime</option>
            <option value="standby">Standby</option>
            <option value="toil">TOIL</option>
          </select>
        </div>
        <button onClick={run} disabled={loading} className="px-3 py-1.5 text-[10px] font-semibold bg-navy text-white rounded hover:bg-navy/80 disabled:opacity-50">
          {loading ? 'Loading…' : 'Run Report'}
        </button>
      </div>

      {data && (
        <>
          {/* Some people have no pay figures on record, so their cost cannot
              be worked out. Said here because a blank column otherwise reads
              as nothing having been worked. */}
          {data.has_missing_rate && (
            <div className="bg-amber-50 border border-amber-200 rounded p-2 mb-3 text-[9px] text-amber-700">
              ⚠ Some people have no pay figures on record, so their cost shows as <em>—</em>. HR can complete them in the People section.
            </div>
          )}

          {/* Summary cards */}
          {data.summary?.length > 0 && (
            <div className="bg-white border border-gray-200 rounded mb-3">
              <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-[10px] font-bold">Summary by Person & Type</h3>
                <button
                  onClick={() => downloadXlsx([{
                    name: 'Summary',
                    rows: data.summary.map(s => ({
                      Person:        s.person_name,
                      Team:          s.team_name || '',
                      Type:          s.request_type,
                      Entries:       s.entries,
                      'Total Hours': s.total_hours,
                      'Total Cost':  s.total_cost != null ? Number(s.total_cost).toFixed(2) : '',
                    })),
                  }], `overtime-summary-${from}-${to}.xlsx`)}
                  className="px-2 py-1 text-[9px] font-semibold bg-emerald-600 text-white rounded hover:bg-emerald-700"
                >
                  ↓ Export
                </button>
              </div>
              <div className="overflow-x-auto"><table className="w-full">
                <thead>
                  <tr>
                    <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Person</th>
                    <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Team</th>
                    <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Type</th>
                    <th className="px-2 py-1 text-right text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Entries</th>
                    <th className="px-2 py-1 text-right text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Total Hours</th>
                    <th className="px-2 py-1 text-right text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Total Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {data.summary.map((s, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-2 py-1 text-[10px] font-semibold">{s.person_name}</td>
                      <td className="px-2 py-1 text-[9px] text-gray-500">{s.team_name || '—'}</td>
                      <td className="px-2 py-1"><span className="text-[9px] font-semibold capitalize">{s.request_type}</span></td>
                      <td className="px-2 py-1 text-[10px] text-right">{s.entries}</td>
                      <td className="px-2 py-1 text-[11px] font-bold text-right text-navy">{hm(s.total_hours)}</td>
                      <td className="px-2 py-1 text-[11px] font-bold text-right text-emerald-700">
                        {s.total_cost != null ? `£${Number(s.total_cost).toFixed(2)}` : <span className="text-gray-300">—</span>}
                      </td>
                    </tr>
                  ))}
                  {/* Grand total row */}
                  <tr className="bg-navy/5 border-t border-gray-200">
                    <td className="px-2 py-1 text-[9px] font-bold text-gray-600" colSpan="4">Grand Total</td>
                    <td className="px-2 py-1 text-[11px] font-bold text-right text-navy">
                      {hm(data.summary.reduce((s, r) => s + (r.total_hours || 0), 0))}
                    </td>
                    <td className="px-2 py-1 text-[11px] font-bold text-right text-emerald-700">
                      {data.summary.every(r => r.total_cost != null)
                        ? `£${data.summary.reduce((s, r) => s + (r.total_cost || 0), 0).toFixed(2)}`
                        : <span className="text-gray-400">partial</span>}
                    </td>
                  </tr>
                </tbody>
              </table></div>
            </div>
          )}

          {/* Detail rows */}
          <div className="bg-white border border-gray-200 rounded">
            <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-[10px] font-bold">Approved Entries ({data.rows?.length || 0})</h3>
              {data.rows?.length > 0 && (
                <button
                  onClick={() => downloadXlsx([{
                    name: 'Approved Entries',
                    rows: data.rows.map(r => ({
                      Person:         r.person_name,
                      Team:           r.team_name || '',
                      Type:           r.request_type,
                      Date:           r.date,
                      Hours:          r.hours,
                      Cost:           r.cost        != null ? Number(r.cost).toFixed(2)        : '',
                      Reason:         r.reason || '',
                      'Approved By':  r.approver_name || '',
                    })),
                  }], `overtime-entries-${from}-${to}.xlsx`)}
                  className="px-2 py-1 text-[9px] font-semibold bg-emerald-600 text-white rounded hover:bg-emerald-700"
                >
                  ↓ Export
                </button>
              )}
            </div>
            {data.rows?.length === 0 ? (
              <div className="text-center py-6 text-[10px] text-gray-400">No approved entries in this period</div>
            ) : (
              <div className="overflow-x-auto"><table className="w-full">
                <thead>
                  <tr>
                    <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Person</th>
                    <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Team</th>
                    <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Type</th>
                    <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Date</th>
                    <th className="px-2 py-1 text-right text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Hours</th>
                    <th className="px-2 py-1 text-right text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Cost</th>
                    <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Reason</th>
                    <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Approved By</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map(r => (
                    <tr key={r.id} className="hover:bg-gray-50">
                      <td className="px-2 py-1 text-[10px] font-semibold">{r.person_name}</td>
                      <td className="px-2 py-1 text-[9px] text-gray-500">{r.team_name || '—'}</td>
                      <td className="px-2 py-1 text-[9px] capitalize">{r.request_type}</td>
                      <td className="px-2 py-1 text-[9px]">{formatDate(r.date)}</td>
                      <td className="px-2 py-1 text-[10px] font-semibold text-right">{hm(r.hours)}</td>
                      <td className="px-2 py-1 text-[10px] font-semibold text-right text-emerald-700">
                        {r.cost != null ? `£${Number(r.cost).toFixed(2)}` : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-2 py-1 text-[9px] text-gray-400 max-w-[100px] truncate">{r.reason || '—'}</td>
                      <td className="px-2 py-1 text-[9px] text-gray-500">{r.approver_name || '—'}</td>
                    </tr>
                  ))}
                  {/* Grand total */}
                  <tr className="bg-navy/5 border-t border-gray-200">
                    <td className="px-2 py-1 text-[9px] font-bold text-gray-600" colSpan="4">Total</td>
                    <td className="px-2 py-1 text-[10px] font-bold text-right text-navy">
                      {hm(data.rows.reduce((s, r) => s + (parseFloat(r.hours) || 0), 0))}
                    </td>
                    <td className="px-2 py-1 text-[10px] font-bold text-right text-emerald-700">
                      {data.rows.some(r => r.cost == null)
                        ? <span className="text-gray-400">partial</span>
                        : `£${data.rows.reduce((s, r) => s + (r.cost || 0), 0).toFixed(2)}`}
                    </td>
                    <td colSpan="2"></td>
                  </tr>
                </tbody>
              </table></div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ── Main page (managers and approvers — employees use My Info) ───────────────
// `embedded` is set when this is a tab of the Approvals page, which has
// already said what the screen is: a second heading under the tab strip
// only repeats it.
//
// Which tabs appear is the grants table's decision, read from /rbac/config,
// by the same keys the server enforces: overtime:approve lists and decides
// every claim, payroll:overtime serves the pay report, overtime:delete removes
// a claim. Naming roles here would let the page contradict what an
// administrator has granted in Admin → Access.
export default function OvertimePage({ embedded = false }) {
  const { currentUser } = useApp();

  // Default to 'team'; 'all' and 'report' follow the grants above
  const [tab, setTab]         = useState('team');

  // Vacancies awaiting approval. The API decides who is an approver (admin, or
  // the person named in settings) and returns an empty list for anyone else,
  // so the tab only appears when there is something for this person to decide.
  const [requests, setRequests] = useState([]);
  const [selected, setSelected] = useState(null);
  const [typeFilter, setTypeFilter] = useState('');
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(() => {
    api.get('overtime-requests?status=pending').then(data => {
      const all = Array.isArray(data) ? data : [];
      // Show all requests that aren't the current user's own (i.e. team members)
      setRequests(all.filter(r => r.person_id !== currentUser?.id));
    });
  }, [currentUser?.id]);

  // For the "all" view (holders of overtime:approve), load without status filter — includes the current user's own requests
  const loadAll = useCallback(() => {
    api.get('overtime-requests').then(data => {
      setRequests(Array.isArray(data) ? data : []);
    });
  }, []);

  useEffect(() => {
    if (tab === 'team') load();
    else if (tab === 'all') loadAll();
  }, [tab, load, loadAll]);

  const handleSave = async (data) => {
    await api.put(`overtime-requests/${data.id}`, data);
    setSelected(null);
    tab === 'team' ? load() : loadAll();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this request? This cannot be undone.')) return;
    setDeletingId(id);
    try {
      await api.delete(`overtime-requests/${id}`);
      tab === 'team' ? load() : loadAll();
    } finally {
      setDeletingId(null);
    }
  };

  const pending = requests.filter(r => r.status === 'pending');
  // The Team Approvals pill is what waits on you, the same figure as the
  // Approvals tab and the sidebar. On the All Requests view the list holds
  // your own claims too, which are not yours to decide.
  const awaitingYou = overtimeAwaitingDecision(requests, currentUser?.id);
  const filteredRequests = typeFilter ? requests.filter(r => r.request_type === typeFilter) : requests;

  const tabs = [
    { id: 'team',   label: 'Team Approvals' },
    ...(canAccess('overtime:approve') ? [{ id: 'all', label: 'All Requests' }] : []),
    ...(canAccess('payroll:overtime') ? [{ id: 'report', label: 'Report' }] : []),
  ];

  return (
    <div className="fade-in">
      {/* Header */}
      {!embedded && (
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-base font-extrabold text-navy">Approval Requests</h2>
            <div className="text-[9px] text-gray-500">Review overtime, standby, call-out, and allowance requests from your team</div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-3">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-3 py-1 text-[10px] font-semibold rounded ${tab === t.id ? 'bg-navy text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
            {t.label}
            {t.id === 'team' && awaitingYou > 0 && (
              <span className="ml-1 bg-amber-400 text-amber-950 text-[8px] font-bold px-1 rounded-full">{awaitingYou}</span>
            )}
          </button>
        ))}
      </div>

      {/* Report tab */}
      {tab === 'report' && <ReportView />}

      {/* Team / All tabs */}
      {tab !== 'report' && (
        <>
          {/* Stats */}
          <div className="grid grid-cols-3 gap-2 mb-3">
            <div className="bg-white border border-gray-200 rounded p-2">
              <div className="text-[8px] text-gray-500 uppercase font-bold">Pending</div>
              <div className={`text-lg font-extrabold ${pending.length ? 'text-amber-500' : 'text-navy'}`}>{pending.length}</div>
            </div>
            <div className="bg-white border border-gray-200 rounded p-2">
              <div className="text-[8px] text-gray-500 uppercase font-bold">Shown</div>
              <div className="text-lg font-extrabold text-navy">{requests.length}</div>
            </div>
            <div className="bg-white border border-gray-200 rounded p-2">
              <div className="text-[8px] text-gray-500 uppercase font-bold">Approved Hours</div>
              <div className="text-lg font-extrabold text-navy">
                {hm(requests.filter(r => r.status === 'approved').reduce((s, r) => s + (parseFloat(r.hours) || 0), 0))}
              </div>
            </div>
          </div>

          {/* Type filter */}
          <div className="flex flex-wrap gap-1 mb-2">
            {['', 'overtime', 'toil', 'standby', 'call_out', 'shift_move', 'working_away', 'desk_move_weekday', 'desk_move_weekend', 'remote_working', 'out_of_hours'].map(t => (
              <button key={t} onClick={() => setTypeFilter(t)}
                className={`px-2 py-0.5 text-[9px] font-semibold rounded border transition-all ${
                  typeFilter === t ? 'bg-navy text-white border-navy' : 'bg-white border-gray-200 text-gray-500 hover:border-navy/40'
                }`}>
                {t === '' ? 'All Types' : (TYPE_LABELS[t] || t)}
              </button>
            ))}
          </div>

          {/* Table */}
          <div className="bg-white border border-gray-200 rounded">
            <div className="overflow-x-auto"><table className="w-full">
              <thead>
                <tr>
                  <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Person</th>
                  <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Type</th>
                  <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Start</th>
                  <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">End</th>
                  <th className="px-2 py-1 text-right text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Hours</th>
                  <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Status</th>
                  <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Description</th>
                  <th className="px-2 py-1 border-b bg-gray-50"></th>
                </tr>
              </thead>
              <tbody>
                {filteredRequests.map(r => (
                  <tr key={r.id} className="hover:bg-gold/5">
                    <td className="px-2 py-1 text-[10px] font-semibold">{r.person_name}</td>
                    <td className="px-2 py-1 text-[10px] capitalize">{TYPE_LABELS[r.request_type] || r.request_type}</td>
                    <td className="px-2 py-1 text-[9px]">{formatDatetime(r.start_datetime || r.date)}</td>
                    <td className="px-2 py-1 text-[9px]">{r.end_datetime ? formatDatetime(r.end_datetime) : '—'}</td>
                    <td className="px-2 py-1 text-[10px] font-semibold text-right">{hm(r.hours)}</td>
                    <td className="px-2 py-1"><StatusBadge status={r.status} /></td>
                    <td className="px-2 py-1 text-[9px] text-gray-400 max-w-[100px] truncate">{r.reason || '—'}</td>
                    <td className="px-2 py-1 flex items-center gap-2">
                      <button onClick={() => setSelected(r)} className="text-[9px] text-navy underline hover:text-gold">
                        {r.status === 'pending' ? 'Review' : 'View'}
                      </button>
                      {canAccess('overtime:delete') && tab === 'all' && (
                        <button
                          onClick={() => handleDelete(r.id)}
                          disabled={deletingId === r.id}
                          className="text-[9px] text-red-400 hover:text-red-600 disabled:opacity-40"
                          title="Delete this request"
                        >
                          {deletingId === r.id ? '…' : '✕'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {filteredRequests.length === 0 && (
                  <tr><td colSpan="8" className="text-center py-6 text-[10px] text-gray-400">
                    {tab === 'team' ? 'No pending requests from your team' : typeFilter ? `No ${TYPE_LABELS[typeFilter] || typeFilter} requests found` : 'No requests found'}
                  </td></tr>
                )}
              </tbody>
            </table></div>
          </div>
        </>
      )}

      {/* Review modal */}
      {selected && (
        <OvertimeModal
          req={selected}
          onClose={() => setSelected(null)}
          onSave={handleSave}
          isManager={true}
        />
      )}
    </div>
  );
}
